import styled from "styled-components";
import { Container } from "./Container";
import { WorkFlow } from "./WorkFlow";

const Section = styled.section`
  padding: var(--section-y) 0;
`;

const SectionHeader = styled.div`
  display: grid;
  gap: 12px;
  margin-bottom: clamp(28px, 4vw, 52px);
  text-align: center;
  justify-items: center;
`;

const Title = styled.h2`
  font-size: clamp(30px, 3.6vw, 46px);
  line-height: 1.08;
  letter-spacing: -0.03em;
  color: var(--text-0);
`;

const Group = styled.div`
  margin-top: 30px;
`;

const GroupTitleRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: center;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 14px;
  text-align: center;
`;

const GroupTitle = styled.h3`
  color: var(--text-0);
  font-size: 19px;
  letter-spacing: 0.01em;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 18px;
  align-items: stretch;
`;

const WebGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 18px;
  align-items: stretch;

  @media screen and (max-width: 860px) {
    grid-template-columns: 1fr;
  }
`;

const CardBase = styled.article`
  border: 1px solid var(--border-0);
  background: var(--surface-0);
  border-radius: var(--radius-lg);
  overflow: hidden;
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.18) inset;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;

  transition:
    transform 180ms ease,
    border-color 180ms ease,
    background 180ms ease;

  &:hover {
    transform: translateY(-2px);
    background: var(--surface-1);
    border-color: rgba(255, 255, 255, 0.18);
  }
`;

const CardOuter = styled.div`
  display: flex;
  height: 100%;
  width: 100%;
  color: inherit;
  text-decoration: none;
`;

const CardMedia = styled.div`
  position: relative;
  height: clamp(148px, 13vw, 190px);
  background: var(--bg-1);
  overflow: hidden;
`;

const CardImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transform: scale(1.02);
`;

const CardBody = styled.div`
  padding: 18px 18px 20px;
  display: grid;
  gap: 10px;
  flex: 1 1 auto;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
`;

const CardTitle = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
`;

const CardName = styled.h4`
  color: var(--text-0);
  font-size: 17px;
  line-height: 1.2;
`;

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 6px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 750;
  letter-spacing: 0.02em;

  border: 1px solid rgba(255, 255, 255, 0.14);
  background: rgba(255, 255, 255, 0.05);
  color: rgba(255, 255, 255, 0.82);
`;

const AccentBadge = styled(Badge)`
  border-color: rgba(185, 255, 102, 0.38);
  background: rgba(185, 255, 102, 0.1);
  color: rgba(255, 255, 255, 0.88);
`;

const CardDesc = styled.p`
  font-size: 15px;
`;

const Process = styled.div`
  margin-top: clamp(52px, 7vw, 86px);
`;

const ProcessHeader = styled.div`
  display: grid;
  gap: 10px;
  margin-bottom: 18px;
  text-align: center;
  justify-items: center;
`;

const ProcessTitle = styled.h3`
  color: var(--text-0);
  font-size: 24px;
  letter-spacing: -0.02em;
`;

const WorkCard = ({ href, image, name, badge, description }) => {
  const card = (
    <CardBase>
      <CardMedia>
        <CardImage src={image} alt={name} loading="lazy" />
      </CardMedia>
      <CardBody>
        <CardTitle>
          <CardName>{name}</CardName>
          {badge ? <AccentBadge>{badge}</AccentBadge> : null}
        </CardTitle>
        <CardDesc>{description}</CardDesc>
      </CardBody>
    </CardBase>
  );

  if (!href) return <CardOuter>{card}</CardOuter>;

  return (
    <CardOuter as="a" href={href} target="_blank" rel="noreferrer">
      {card}
    </CardOuter>
  );
};

export const OurWork = () => {
  return (
    <Section id="work">
      <Container>
        <SectionHeader>
          <Title>The Work</Title>
        </SectionHeader>

        <Group>
          <GroupTitleRow>
            <GroupTitle>Web Development</GroupTitle>
          </GroupTitleRow>
          <WebGrid>
            <WorkCard
              href="https://lebrunshvac.com"
              image={require("../assets/images/lebrunshvac.png")}
              name="Lebrun's HVAC Services"
              badge="Live"
              description="Created a static website with a functioning email form working using PHP."
            />
            <WorkCard
              href="https://puppizzadogbakery.com/"
              image={require("../assets/images/puppizzabakery.png")}
              name="Pup Pizza Dog Bakery"
              badge="Live"
              description="A Shopify ecommerce application for Pup Pizza Dog Bakery—storefront build, theme customization, and a smooth checkout flow."
            />
            <WorkCard
              image={require("../assets/images/bonzanmi.png")}
              name="Bon Zanmi Haitian Cuisine"
              badge="In Progress"
              description="Developed from scratch, a Web Application that allows users the ability to place food orders online by credit card - including email and text automation to alert users of order statuses."
            />
            <WorkCard
              href="https://www.noteworthy.ai/blog/announcing-inspect-cloud-2-0-a-new-era-of-real-time-map-centric-asset-management"
              image={require("../assets/images/inspect cloud.png")}
              name="Inspect Cloud 2.0"
              badge="Live"
              description="Foundational work for NoteworthyAI's Inspect Cloud 2.0 (real-time, map-centric asset management)—helped construct the frontend/backend foundations and get CI/CD hooked up using AWS and GitHub Actions."
            />
          </WebGrid>
        </Group>

        <Group>
          <GroupTitleRow>
            <GroupTitle>Desktop Application Development</GroupTitle>
          </GroupTitleRow>
          <Grid>
            <WorkCard
              image={require("../assets/images/roku_remote.png")}
              name="Roku Remote"
              badge="Desktop"
              description="A Desktop application, that when connected to the same Wi-Fi network of Roku Device(s) allows a user to control it from their keyboard, and adjust television settings."
            />
            <WorkCard
              href="https://www.cuescript.tv/cueiT-software.php"
              image={require("../assets/images/cueit.jpeg")}
              name="Cue-iT"
              badge="Enterprise"
              description="Developed new features and provided maintenance on CueScript's Enterprise Teleprompting Desktop Application used in the Broadcasting industry worldwide."
            />
            <WorkCard
              image={require("../assets/images/stat_crawler.png")}
              name="Stat Crawler"
              badge="Desktop"
              description="A Desktop Application that allows users to crawl and compare statistical data of individual NBA players (leveraging the balldontlie API)."
            />
          </Grid>
        </Group>

        <Process>
          <ProcessHeader>
            <ProcessTitle>How We Get It Done</ProcessTitle>
          </ProcessHeader>
          <WorkFlow />
        </Process>
      </Container>
    </Section>
  );
};
